import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Image, 
  FolderOpen, 
  Mail, 
  Settings, 
  Eye,
  LogOut,
  Sun,
  Moon,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { useState } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5c120cb0-6c39-4574-8992-e03c5cf62daa/artifacts/58vxcyd0_navlogo_2.png";

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/photos', icon: Image, label: 'Photos' },
  { path: '/admin/categories', icon: FolderOpen, label: 'Catégories' },
  { path: '/admin/messages', icon: Mail, label: 'Messages' },
  { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
  { path: '/admin/preview', icon: Eye, label: 'Aperçu' },
];

export function AdminSidebar() {
  const location = useLocation();
  const { logout, admin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
          <img src={LOGO_URL} alt="Eddy Dossou" className="h-8 w-auto" />
          <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Admin</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-colors ${
              isActive(item.path, item.exact)
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border space-y-3">
        {/* Theme Toggle */}
        <Button 
          variant="ghost" 
          onClick={toggleTheme}
          className="w-full justify-start gap-3"
          data-testid="admin-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
        </Button>

        {/* View Site */}
        <Link to="/" target="_blank">
          <Button variant="outline" className="w-full justify-start gap-3" data-testid="admin-view-site">
            <Eye className="h-5 w-5" />
            Voir le site
          </Button>
        </Link>

        {/* User & Logout */}
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2 truncate">{admin?.email}</p>
          <Button 
            variant="ghost" 
            onClick={logout}
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5" />
            Déconnexion
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border p-4 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Eddy Dossou" className="h-8 w-auto" />
          <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Admin</span>
        </Link>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="admin-mobile-menu">
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
          <div className="fixed left-0 top-0 bottom-0 w-72 bg-card border-r border-border flex flex-col" onClick={e => e.stopPropagation()}>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-card border-r border-border flex-col fixed left-0 top-0 bottom-0 z-40">
        <SidebarContent />
      </aside>
    </>
  );
}
